import * as React from "react";

import { elementContains, getRTL } from "@fluentui/react/lib/Utilities";

import {
  AppPaddleButton,
  appPaddleWidth,
  PaddleOrientation,
} from "../AppPaddleButton";
import type { AppOverflowPaddleControlProps } from "./AppOverflowPaddleControl.Props";
import {
  leftPaddleId,
  leftPaddleSelector,
  rightPaddleId,
  rightPaddleSelector,
} from "./AppOverflowPaddleConstants";
import { AppContext } from "@1js/acui-context";

export interface AppOverflowPaddleControlState {
  paddleOrientation: PaddleOrientation;
}

export class AppOverflowPaddleControl extends React.PureComponent<
  React.PropsWithChildren<AppOverflowPaddleControlProps>,
  AppOverflowPaddleControlState
> {
  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  private container = React.createRef<HTMLDivElement>();

  // Number of pixels we will allow the calculations to be off by.
  private paddlePixelThreshold = 1;
  private isRTL = false;
  private leftPaddleLabel = "";
  private rightPaddleLabel = "";

  constructor(props: AppOverflowPaddleControlProps) {
    super(props);
    this.isRTL = getRTL();
    if (this.isRTL) {
      this.leftPaddleLabel = this.props.rightRibbonPaddleLabel;
      this.rightPaddleLabel = this.props.leftRibbonPaddleLabel;
    } else {
      this.leftPaddleLabel = this.props.leftRibbonPaddleLabel;
      this.rightPaddleLabel = this.props.rightRibbonPaddleLabel;
    }

    this.state = {
      paddleOrientation: PaddleOrientation.Hidden,
    };
  }

  public render() {
    const { downChevronIcon } = this.props;

    return (
      <div ref={this.container} id="paddleContainer">
        {this.props.children}
        {(this.state.paddleOrientation === PaddleOrientation.Right ||
          this.state.paddleOrientation === PaddleOrientation.LeftRight) && (
          <AppPaddleButton
            id={rightPaddleId}
            icon={downChevronIcon}
            orientation={PaddleOrientation.Right}
            onExecute={this.onPaddleButtonExecute}
            label={this.rightPaddleLabel}
            disableModeTogglePadding={this.props.disableModeTogglePadding}
          />
        )}
        {(this.state.paddleOrientation === PaddleOrientation.Left ||
          this.state.paddleOrientation === PaddleOrientation.LeftRight) && (
          <AppPaddleButton
            id={leftPaddleId}
            icon={downChevronIcon}
            orientation={PaddleOrientation.Left}
            onExecute={this.onPaddleButtonExecute}
            label={this.leftPaddleLabel}
            disableModeTogglePadding={this.props.disableModeTogglePadding}
          />
        )}
      </div>
    );
  }

  public componentDidUpdate(
    _prevProps: AppOverflowPaddleControlProps,
    prevState: AppOverflowPaddleControlState
  ) {
    if (
      prevState.paddleOrientation === PaddleOrientation.Hidden &&
      this.state.paddleOrientation !== PaddleOrientation.Hidden
    ) {
      this.props.onPaddlesShown?.();
    }
  }

  public isPaddlesVisible = () => {
    return this.state.paddleOrientation !== PaddleOrientation.Hidden;
  };

  public isEndPaddleVisible = () => {
    return (
      this.state.paddleOrientation === PaddleOrientation.Right ||
      this.state.paddleOrientation === PaddleOrientation.LeftRight
    );
  };

  public hideRibbonPaddles = () => {
    this.setState({ paddleOrientation: PaddleOrientation.Hidden });
  };

  private getScrollWidth() {
    const { innerChildContainerId } = this.props;

    const innerChildContainer = innerChildContainerId
      ? document.getElementById(innerChildContainerId)
      : null;

    if (
      this.container.current &&
      innerChildContainer &&
      elementContains(this.container.current, innerChildContainer)
    ) {
      return innerChildContainer.clientWidth;
    }
    return undefined;
  }

  /**
   * Function that handles determining if we should add paddles to the ribbon
   */
  public handleChildOverflow = () => {
    if (this.container.current && this.container.current.children) {
      const overflowElement = this.container.current.children[0];
      const scrollWidth = this.getScrollWidth() || overflowElement.scrollWidth;

      if (scrollWidth > overflowElement.clientWidth) {
        if (
          !!this.props.onContentOverflow?.(
            scrollWidth,
            scrollWidth - overflowElement.clientWidth
          )
        ) {
          // The orchestrator has taken steps to shrink the rendered content
          return;
        }

        if (this.isRTL) {
          {
            this.handleChildOverflowRightToLeft(
              overflowElement,
              scrollWidth,
              overflowElement.clientWidth
            );
          }
        } else {
          this.handleChildOverflowLeftToRight(
            overflowElement,
            scrollWidth,
            overflowElement.clientWidth
          );
        }
      } else {
        this.setState({ paddleOrientation: PaddleOrientation.Hidden });
      }
    }
  };

  /**
   * Allow the browser to scroll element into view, and then
   * readjust the scroll offset if the focused element
   * will still be partially obscured by the paddles.
   */
  public scrollActiveElementIntoView = (activeElement: HTMLElement) => {
    activeElement.scrollIntoView({ block: "nearest" });

    if (!this.container.current) return;
    const overflowElement = this.container.current.children[0];
    const elementRect = activeElement.getBoundingClientRect();
    const orientation = this.getPaddleOriginationBasedOnRTL();

    // Check if "left" paddle is blocking
    if (
      orientation === PaddleOrientation.Left ||
      orientation === PaddleOrientation.LeftRight
    ) {
      const paddleQuerySelector = this.isRTL
        ? rightPaddleSelector
        : leftPaddleSelector;
      const lPaddle = document.querySelector(paddleQuerySelector);
      if (lPaddle) {
        const paddleRect = lPaddle.getBoundingClientRect();
        const paddleRightCorner = paddleRect.x + paddleRect.width;
        if (elementRect.x < paddleRightCorner) {
          overflowElement.scrollLeft -= paddleRightCorner - elementRect.x;
          return;
        }
      }
    }

    // Check if "right" paddle is blocking
    if (
      orientation === PaddleOrientation.Right ||
      orientation === PaddleOrientation.LeftRight
    ) {
      const paddleQuerySelector = this.isRTL
        ? leftPaddleSelector
        : rightPaddleSelector;
      const rPaddle = document.querySelector(paddleQuerySelector);
      if (rPaddle) {
        const paddleRect = rPaddle.getBoundingClientRect();
        if (paddleRect.x < elementRect.x + elementRect.width) {
          overflowElement.scrollLeft +=
            elementRect.x + elementRect.width - paddleRect.x;
        }
      }
    }
  };

  private handleChildOverflowRightToLeft = (
    overflowElement: Element,
    scrollWidth: number,
    clientWidth: number
  ) => {
    // Ribbon paddles will be add if there is content (scrollWidth) outside of what can be seen (clientWidth).
    // If we have the scrollWidth-scrollLeft, which is > 0 in RTL if any content is off screen
    // is less than the clientWidth than we just need to add a paddle to the left.
    // If we have some amount of content off the screen to the left, but the width is
    // still greater than the clientWidth we need to add a paddle on both sides.  However, if the scrollLeft
    // is less than the pixels of the paddle, we will scroll over the additional amount to avoid
    // a control being slightly cut off.
    // Else we add it to just the left
    if (
      scrollWidth - Math.abs(overflowElement.scrollLeft) <=
      clientWidth + this.paddlePixelThreshold
    ) {
      // If the content that remains off screener is less than the size of the paddle, scroll
      // it into view
      this.setState({ paddleOrientation: PaddleOrientation.Left });
    } else if (
      scrollWidth - Math.abs(overflowElement.scrollLeft) >
      clientWidth
    ) {
      if (Math.abs(overflowElement.scrollLeft) < appPaddleWidth) {
        overflowElement.scrollLeft = 0;
        this.setState({ paddleOrientation: PaddleOrientation.Right });
        return;
      }
      this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
    } else {
      this.setState({ paddleOrientation: PaddleOrientation.Right });
    }
  };

  private handleChildOverflowLeftToRight = (
    overflowElement: Element,
    scrollWidth: number,
    clientWidth: number
  ) => {
    // Ribbon paddles will be add if there is content (scrollWidth) outside of what can be seen (clientWidth).
    // If we have the scrollWidth is greater than the clientWidth, but the scrollLeft is 0,
    // than we just need to add a paddle to the right.
    // If we have some amount of content off the screen to the left, but we the width is
    // still greater than the clientWidth we need to add a paddle on both sides.
    // Else we add it to just the left
    // If the scrollWidth is less than or equal to the clientWidth the paddles will be hidden
    if (overflowElement.scrollLeft < this.paddlePixelThreshold) {
      // Make sure scrollLeft is 0 if only showing the Right paddle.
      overflowElement.scrollLeft = 0;

      this.setState({ paddleOrientation: PaddleOrientation.Right });
    } else if (scrollWidth - overflowElement.scrollLeft > clientWidth) {
      // If the content that remains off screener is less than the size of the paddle, scroll
      // it into view.
      if (
        scrollWidth - overflowElement.scrollLeft - clientWidth <=
        appPaddleWidth
      ) {
        overflowElement.scrollLeft =
          overflowElement.scrollLeft + appPaddleWidth;
        this.setState({ paddleOrientation: PaddleOrientation.Left });
        return;
      }

      this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
    } else {
      this.setState({ paddleOrientation: PaddleOrientation.Left });
    }
  };

  private getPaddleOriginationBasedOnRTL() {
    if (this.isRTL) {
      switch (this.state.paddleOrientation) {
        case PaddleOrientation.Right:
          return PaddleOrientation.Left;
        case PaddleOrientation.Left:
          return PaddleOrientation.Right;
        case PaddleOrientation.LeftRight:
          return PaddleOrientation.LeftRight;
        default:
          return PaddleOrientation.Hidden;
      }
    }

    return this.state.paddleOrientation;
  }

  /**
   * Function to handle execute of ribbon paddles.
   */
  private onPaddleButtonExecute = (id: string) => {
    this.props.onPaddleExecuted?.(id);

    if (this.container.current && this.container.current.children) {
      const overflowElement = this.container.current.children[0];

      const paddleOrientation = this.state.paddleOrientation;

      const scrollWidth = this.getScrollWidth() || overflowElement.scrollWidth;

      const unseenContentWidthSinglePaddle =
        scrollWidth - overflowElement.clientWidth;
      const unseenContentWidthDoublePaddle =
        unseenContentWidthSinglePaddle - Math.abs(overflowElement.scrollLeft);

      if (paddleOrientation === PaddleOrientation.LeftRight) {
        // If the current state is that both paddles are displayed and the right paddle is executed.
        // If the width of the content that isn't seen by the user is less than the clientWidth, we can
        // scroll to the end and show just the left paddle
        // Otherwise we need to scroll the ribbon to the right the width of the view area and keep
        // both paddles
        // This is the same but in reverse if the LeftPaddle is being executed
        if (unseenContentWidthDoublePaddle === 0) {
          this.setState({ paddleOrientation: PaddleOrientation.Hidden });
          return;
        }

        if (id === rightPaddleId) {
          if (unseenContentWidthDoublePaddle <= overflowElement.clientWidth) {
            overflowElement.scrollLeft = this.isRTL
              ? overflowElement.scrollLeft - unseenContentWidthDoublePaddle
              : overflowElement.scrollLeft + unseenContentWidthDoublePaddle;
            this.setState({ paddleOrientation: PaddleOrientation.Left });
          } else {
            overflowElement.scrollLeft = this.isRTL
              ? overflowElement.scrollLeft - overflowElement.clientWidth
              : overflowElement.scrollLeft + overflowElement.clientWidth;
          }
        } else {
          if (
            Math.abs(overflowElement.scrollLeft) <=
            overflowElement.clientWidth + this.paddlePixelThreshold
          ) {
            overflowElement.scrollLeft = 0;
            this.setState({ paddleOrientation: PaddleOrientation.Right });
          } else {
            overflowElement.scrollLeft = this.isRTL
              ? overflowElement.scrollLeft + overflowElement.clientWidth
              : overflowElement.scrollLeft - overflowElement.clientWidth;
          }
        }
      } else if (paddleOrientation === PaddleOrientation.Right) {
        // If the right paddle has been executed, and the content that is off screen is less
        // than the viewable width then we can scroll the width of the content unseen and show just
        // the left paddle.  If the content is greater, then we are in a middle state and need to
        // show both paddles.
        if (unseenContentWidthSinglePaddle === 0) {
          this.setState({ paddleOrientation: PaddleOrientation.Hidden });
          return;
        }

        if (unseenContentWidthSinglePaddle <= overflowElement.clientWidth) {
          overflowElement.scrollLeft = this.isRTL
            ? -unseenContentWidthSinglePaddle
            : unseenContentWidthSinglePaddle;
          this.setState({ paddleOrientation: PaddleOrientation.Left });
        } else {
          overflowElement.scrollLeft = this.isRTL
            ? -overflowElement.clientWidth
            : overflowElement.clientWidth;
          this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
        }
      } else {
        // Else if the left paddle was executed follow similar logic to the right.
        if (unseenContentWidthSinglePaddle === 0) {
          this.setState({ paddleOrientation: PaddleOrientation.Hidden });
          return;
        }

        if (unseenContentWidthSinglePaddle <= overflowElement.clientWidth) {
          overflowElement.scrollLeft = 0;
          this.setState({ paddleOrientation: PaddleOrientation.Right });
        } else {
          overflowElement.scrollLeft =
            overflowElement.scrollLeft - overflowElement.clientWidth;
          this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
        }
      }
    }
  };
}
